import { useEffect, useState } from "react";
import { Snackbar } from "../common/Snackbar";
import { FormModal } from "../layout/Modal/Modal";
import { SnackbarProps } from "../common/SnackbarProps";
import { ParcelData } from "../../interfaces/ParcelData";
import ParcelService from "../../services/parcelService";
import { useSharedState } from "../../config/FarmContext";
import { ParcelCycle } from "../../interfaces/ParcelCycle";
import TaskOverviewForm from "../form/TaskOverviewForm/TaskOverviewForm";
import { CycleInfoPanel } from "../form/CycleInfoPanel";
import { Pagination } from "../common/Pagination";
import { useAutoPageSize } from "../../hooks/useAutoPageSize";
import { formatDateDisplay } from "../../utils/date";
import {FieldType} from "../../interfaces/FieldType";

export const ParcelList = () => {
    const { farm } = useSharedState();

    const [loading, setLoading] = useState(true);
    const [showModal, setModal] = useState(false);
    const [parcels, setParcels] = useState<{ id: number; parcel: ParcelData }[]>([]);
    const [cycles, setCycles] = useState<ParcelCycle[]>([]);
    const [currentParcel, setCurrentParcel] = useState<{ id: number; parcel: ParcelData }>();
    const [snackbar, setSnackbar] = useState<SnackbarProps>({ active: false });
    const [selectedItem, setSelectedItem] = useState<ParcelCycle | null>(null);
    const [selectedParcelId, setSelectedParcelId] = useState<number | null>(null);

    const [parcelPage, setParcelPage] = useState(1);
    const [cyclePage, setCyclePage] = useState(1);

    // Auto page size hooks
    const {
        containerRef: parcelContainerRef,
        itemRef: parcelItemRef,
        pageSize: parcelPageSize
    } = useAutoPageSize(6);

    const {
        containerRef: cycleContainerRef,
        itemRef: cycleItemRef,
        pageSize: cyclePageSize
    } = useAutoPageSize(6);

    const parcelService = new ParcelService();

    useEffect(() => {
        const fetchParcels = async () => {
            try {
                if (!farm?.id) return;
                const response = await parcelService.getParcelByFarmId(farm.id);
                setParcels(response.map((parcel) => ({ id: parcel.id!, parcel })));
            } catch (error) {
                console.error("Error fetching parcels:", error);
            } finally {
                setLoading(false);
            }
        };
        fetchParcels();
    }, [farm]);

    const toggleModal = () => setModal((prev) => !prev);



    const getParcelType = (parcel: ParcelData) => parcel.type === FieldType.Futterbau ? "Futterbau" : "Ackerbau";

    const fetchParcelCycles = async (parcelId: number) => {
        try {
            setLoading(true);
            const cycles = await parcelService.getParcelCycle(parcelId);

            setCycles(
                cycles.sort((a, b) => {
                    if (!a.endDate) return -1;
                    if (!b.endDate) return 1;
                    return new Date(b.endDate).getTime() - new Date(a.endDate).getTime();
                })
            );
        } catch (error) {
            triggerSnackbar("Zyklen konnten nicht geladen werden ❌", "error");
        } finally {
            setLoading(false);
        }
    };

    const handleParcelClick = (parcelId: number) => {
        if (selectedParcelId === parcelId) return;

        setSelectedParcelId(parcelId);
        setSelectedItem(null);
        setCurrentParcel(parcels.find((p) => p.id === parcelId));
        setCycles([]);
        setCyclePage(1);
        fetchParcelCycles(parcelId);
    };

    const triggerSnackbar = (message: string, type: "success" | "error" | "info" | "warning") => {
        setSnackbar({ message, type, active: true });
        setTimeout(() => setSnackbar({ active: false }), 3000);
    };

    const formatInputDate = (dateString: string | Date | undefined) => {
        if (!dateString) return "";
        const date = dateString instanceof Date ? dateString : new Date(dateString);
        return formatDateDisplay(date as Date);
    };

    // Paginated data
    const totalParcelPages = Math.ceil(parcels.length / parcelPageSize);
    const parcelStartIndex = (parcelPage - 1) * parcelPageSize;
    const currentParcels = parcels.slice(parcelStartIndex, parcelStartIndex + parcelPageSize);

    const totalCyclePages = Math.ceil(cycles.length / cyclePageSize);
    const cycleStartIndex = (cyclePage - 1) * cyclePageSize;
    const currentCycles = cycles.slice(cycleStartIndex, cycleStartIndex + cyclePageSize);

    return (
        <div className="flex flex-col md:flex-row gap-2 h-full">

            {/* Parcels column */}
            <div className="flex-1 flex flex-col border border-gray-300 p-3 rounded-lg bg-white shadow">
                <h3 className="font-semibold mb-2">Parzellen ({parcels.length})</h3>

                <ul ref={parcelContainerRef} className="flex-1 overflow-y-auto pr-1">
                    {currentParcels.map((parcel, idx) => (
                        <li
                            key={parcel.id}
                            ref={idx === 0 ? parcelItemRef : null}
                            onClick={() => handleParcelClick(parcel.id)}
                            className={`p-2 my-1 font-semibold border rounded-md cursor-pointer transition-colors 
        ${selectedParcelId === parcel.id
                                    ? "bg-green-900 text-white"
                                    : "bg-gray-50 text-gray-800 hover:bg-gray-100"
                                }`}
                        >
                            <div>{parcel.parcel.name}</div>
                            <span className="block text-xs font-normal opacity-70 mt-1">
                                {getParcelType(parcel.parcel)}  {parcel.parcel.subtype}
                            </span>
                        </li>
                    ))}
                </ul>

                <Pagination
                    currentPage={parcelPage}
                    totalPages={totalParcelPages}
                    onPageChange={setParcelPage}
                />
            </div>

            {/* Cycles column */}
            <div className="flex-1 flex flex-col border border-gray-300 p-3 rounded-lg bg-white shadow">
                <h3 className="font-semibold mb-2">Zyklen ({cycles.length})</h3>

                {selectedParcelId ? (
                    <>
                        <ul ref={cycleContainerRef} className="flex-1 overflow-y-auto pr-1">
                            {currentCycles.map((cycle, idx) => (
                                <li
                                    key={idx}
                                    ref={idx === 0 ? cycleItemRef : null}
                                    onClick={() => setSelectedItem(cycle)}
                                    className={`p-2 my-1 font-semibold border rounded-md cursor-pointer transition-colors 
                                        ${selectedItem === cycle
                                            ? "bg-green-900 text-white"
                                            : "bg-gray-50 text-gray-800 hover:bg-gray-100"
                                        }`}
                                >
                                    {formatInputDate(cycle.startDate)} – {cycle.endDate ? formatInputDate(cycle.endDate) : "heute"}
                                </li>
                            ))}
                        </ul>

                        <Pagination
                            currentPage={cyclePage}
                            totalPages={totalCyclePages}
                            onPageChange={setCyclePage}
                        />
                    </>
                ) : (
                    <p className="text-gray-500 flex-1 flex items-center justify-center">
                        Wähle eine Parzelle
                    </p>
                )}
            </div>

            {/* Info column */}
            {currentParcel && selectedItem ? (
                <CycleInfoPanel
                    currentParcel={currentParcel.parcel}
                    currentCycle={selectedItem}
                    refreshCycles={fetchParcelCycles}
                    showCycleButton={true}
                    toggleModal={toggleModal}
                />
            ) : (
                <div className="flex-1 border border-gray-300 p-3 min-h-[200px] rounded-lg bg-white shadow flex items-center justify-center text-gray-500">
                    Wähle einen Zyklus
                </div>
            )}

            {/* Snackbar */}
            {snackbar.active && (
                <Snackbar message={snackbar.message!} type={snackbar.type} />
            )}

            {/* Task Modal */}
            {selectedItem && (
                <FormModal open={showModal} onClose={toggleModal}>
                    <TaskOverviewForm
                        parcel={currentParcel?.parcel!}
                        cycle={selectedItem}
                        showParcelForm={false}
                    />
                </FormModal>
            )}
        </div>
    );
};
